/**
 * CHANGE 3. What the right answer was, on every rung, whenever you got it
 * wrong.
 *
 * This is the educational payload of the whole game and the brief is blunt
 * about why it was missing: a wrong probe answer busted the round and the
 * player never learned what the right answer was, "which throws away the entire
 * educational payload at the exact moment it would land hardest". Eight seconds
 * of caring about one country, then nothing.
 *
 * So: the miss is stated plainly, in words, at the top — no hedging, no "not
 * quite". Under it, the player's wrong pick in red beside the right one in
 * green, in the SAME control they answered in, so the eye does not have to
 * re-parse a new layout to find out what happened. The globe is doing the other
 * half at the same time (see `GlobeStage`), spinning to the answer and pulsing
 * a ring on it.
 *
 * INVARIANT 5 IS NOT AT RISK HERE, and it is worth saying why rather than
 * trusting the reader to work it out. Everything this component renders comes
 * out of an `AnswerReveal`, and an `AnswerReveal` cannot be minted for a rung
 * that is still in play — `sealReveal` demands a verdict whose `final` is the
 * literal `true`, and the one frame that describes a live rung declares
 * `reveal?: never`. A rung with a reveal is a rung nobody can still answer.
 */

import type { AnswerReveal, GameMode, RoundPayloadRung } from '@geogame/shared';
import { NUMERIC_SPECS, cityDistanceKm, rungOptions } from '@geogame/shared';
import { byCca3 } from '@geogame/data';
import { bySubdivisionCode } from '@geogame/data/states';

import { points, rungTitle } from '../lib/format';
import { OptionGrid } from './OptionGrid';

export interface RungRevealProps {
  readonly reveal: AnswerReveal;
  /** The rung as the player saw it, for its prompt and its option labels. */
  readonly rung: RoundPayloadRung | undefined;
  /** Whether the answer was right. A correct rung shows nothing; it moves on. */
  readonly correct: boolean;
  readonly golden: boolean;
  /**
   * Which mode this round belongs to, because the same reveal means different
   * things in each: a CITY pinpoint is scored by distance and can only be
   * "locked", while a country or subdivision pinpoint is hit-or-miss and can
   * be missed outright.
   */
  readonly gameMode?: GameMode;
  /**
   * Object URLs for a picture rung's options, if it had any.
   *
   * WITHOUT THESE THE REVEAL IS BLANK, and blank in a way that reads as broken
   * rather than as missing. A flag option carries NO TEXT -- deliberately,
   * because a label naming the country would be the answer readable straight
   * off the DOM -- so the artwork is the entire content of the box. This grid
   * was never given them, so the screen that exists to say "here is the right
   * flag" showed four empty rectangles with one of them outlined in green.
   *
   * The same urls the live rung used. They are minted per round and revoked on
   * teardown, so they are still good here: the reveal lands during round-end,
   * long before the match lets go of them.
   */
  readonly imageUrls?: readonly string[];
}

/** What the player tapped, or what they should have, named. */
function entityName(code: string): string {
  return bySubdivisionCode(code)?.name ?? byCca3(code)?.name ?? code;
}

/** The quantity's value, written the way the prompt asked for it. */
function numericText(quantity: string, value: number): string {
  switch (quantity) {
    case 'population-millions':
      return `${points(value)} million`;
    case 'area-rank':
      return `#${value}`;
    case 'land-borders':
      return value === 1 ? '1 country' : `${value} countries`;
    default:
      return String(value);
  }
}

function OrderingReveal({
  options,
  correctOrder,
  pickedOrder,
}: {
  options: readonly { id: string; label: string }[];
  correctOrder: readonly number[];
  pickedOrder: readonly number[] | null;
}): React.JSX.Element {
  return (
    <ol className="gg-reveal-order">
      {correctOrder.map((optIdx, position) => {
        const wasHere = pickedOrder?.[position];
        const right = wasHere === optIdx;
        return (
          <li key={optIdx} className="gg-reveal-order-row" data-right={right ? 'true' : 'false'}>
            <span className="gg-reveal-order-rank tabular-nums">{position + 1}</span>
            <span className="gg-reveal-order-label">{options[optIdx]?.label ?? '?'}</span>
            {!right && wasHere !== undefined ? (
              <span className="gg-reveal-order-yours">you: {options[wasHere]?.label ?? '?'}</span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export function RungReveal({
  reveal,
  rung,
  correct,
  golden,
  gameMode = 'country',
  imageUrls,
}: RungRevealProps): React.JSX.Element {
  const options = rung ? (rungOptions(rung) ?? []) : [];
  /*
   * Keyed on the MODE, not on whether the reveal happens to carry a label.
   * The subdivision mode carries one too — the round is about Georgia and the
   * card should say so — and reading the label as "this was scored by
   * distance" told a player who had timed out entirely that their pin was
   * "locked".
   */
  const cityPin = gameMode === 'city' && reveal.kind === 'pinpoint';

  return (
    <section
      className="gg-rung-reveal"
      data-correct={correct ? 'true' : 'false'}
      data-golden={golden ? 'true' : 'false'}
      role="status"
    >
      <header className="gg-rung-reveal-head">
        <span className="gg-rung-reveal-verdict">
          {cityPin ? 'Pin locked' : correct ? 'Correct' : 'You missed it'}
        </span>
        {rung ? <span className="gg-rung-reveal-rung">{rungTitle(reveal.rung)}</span> : null}
      </header>

      {rung && reveal.kind !== 'pinpoint' ? (
        <p className="gg-rung-reveal-prompt">{rung.prompt}</p>
      ) : null}

      {reveal.kind === 'pinpoint' ? (
        <div className="gg-rung-reveal-pair">
          <div className="gg-rung-reveal-side" data-tone="correct">
            <span className="gg-rung-reveal-tag">The answer</span>
            <span className="gg-rung-reveal-answer">
              {reveal.correctLabel ?? entityName(reveal.correctCca3)}
            </span>
            {cityPin && reveal.pickedLngLat ? (
              <span className="gg-rung-reveal-distance tabular-nums">
                {Math.round(cityDistanceKm(reveal.pickedLngLat, reveal.correctLngLat)).toLocaleString()} km away
              </span>
            ) : null}
          </div>
          {!cityPin && reveal.pickedCca3 && reveal.pickedCca3 !== reveal.correctCca3 ? (
            <div className="gg-rung-reveal-side" data-tone="wrong">
              <span className="gg-rung-reveal-tag">You tapped</span>
              <span className="gg-rung-reveal-answer">{entityName(reveal.pickedCca3)}</span>
            </div>
          ) : !cityPin && reveal.pickedCca3 === null ? (
            <div className="gg-rung-reveal-side" data-tone="wrong">
              <span className="gg-rung-reveal-tag">You tapped</span>
              <span className="gg-rung-reveal-answer">open ocean</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/*
        A MAP CHOICE, named at last.
        Its options reached the player as four unlabelled dots — the pairing of
        name to place IS the answer, so `publicRound` withholds it — and
        `reveal.labels` is where the two halves are put back together. An
        option grid would be the wrong control here: the player never pressed
        one, and the four buttons would be blank.
      */}
      {/*
        NAMED HERE, PINNED ON THE GLOBE, and the two halves have to say they
        belong together. The globe cannot help: its style carries no glyphs and
        no symbol layer, deliberately, so a city can be shown but never
        labelled. A gold dot and a name in a card are the whole vocabulary
        available, and until this said so they were two unrelated facts on one
        screen -- reported as the answer not being made clear enough.
      */}
      {reveal.kind === 'choice' && reveal.labels ? (
        <>
        <div className="gg-rung-reveal-pair">
          <div className="gg-rung-reveal-side" data-tone="correct">
            <span className="gg-rung-reveal-tag">The capital</span>
            <span className="gg-rung-reveal-answer">
              {reveal.labels[reveal.correctIndex] ?? '?'}
            </span>
          </div>
          {reveal.pickedIndex === null ? (
            <div className="gg-rung-reveal-side" data-tone="wrong">
              <span className="gg-rung-reveal-tag">You</span>
              <span className="gg-rung-reveal-answer">ran out of time</span>
            </div>
          ) : reveal.pickedIndex !== reveal.correctIndex ? (
            <div className="gg-rung-reveal-side" data-tone="wrong">
              <span className="gg-rung-reveal-tag">You tapped</span>
              <span className="gg-rung-reveal-answer">
                {reveal.labels[reveal.pickedIndex] ?? '?'}
              </span>
            </div>
          ) : null}
        </div>
        {reveal.pickedIndex !== reveal.correctIndex ? (
          <p className="gg-rung-reveal-where">
            Gold on the map is {reveal.labels[reveal.correctIndex] ?? 'the capital'}
            {reveal.pickedIndex === null ? '.' : ', blue is where you tapped.'}
          </p>
        ) : null}
        </>
      ) : reveal.kind === 'choice' && options.length > 0 ? (
        <OptionGrid
          options={options}
          {...(imageUrls ? { imageUrls } : {})}
          disabled
          chosenIdx={reveal.pickedIndex}
          correctIdx={reveal.correctIndex}
          onChoose={() => undefined}
        />
      ) : null}

      {reveal.kind === 'boolean' ? (
        <>
          <div className="gg-rung-reveal-pair">
            <div className="gg-rung-reveal-side" data-tone="correct">
              <span className="gg-rung-reveal-tag">That statement is</span>
              <span className="gg-rung-reveal-answer">
                {reveal.statementIsTrue ? 'True' : 'False'}
              </span>
            </div>
            {reveal.pickedIndex === null ? (
              <div className="gg-rung-reveal-side" data-tone="wrong">
                <span className="gg-rung-reveal-tag">You</span>
                <span className="gg-rung-reveal-answer">ran out of time</span>
              </div>
            ) : reveal.pickedIndex !== reveal.correctIndex ? (
              <div className="gg-rung-reveal-side" data-tone="wrong">
                <span className="gg-rung-reveal-tag">You said</span>
                <span className="gg-rung-reveal-answer">
                  {reveal.statementIsTrue ? 'False' : 'True'}
                </span>
              </div>
            ) : null}
          </div>

          {/*
            THE FACT, not just the verdict.
            "False" tells a player nothing they can carry to the next round —
            the thing worth learning from "Sydney is the capital of Australia"
            is that Canberra is. Shown only when the statement was FALSE, since
            on a true one the prompt they just read already IS the fact.
          */}
          {reveal.correction && !reveal.statementIsTrue ? (
            <p className="gg-rung-reveal-fact">{reveal.correction}</p>
          ) : null}
        </>
      ) : null}

      {reveal.kind === 'ordering' && options.length > 0 ? (
        <OrderingReveal
          options={options}
          correctOrder={reveal.correctOrder}
          pickedOrder={reveal.pickedOrder}
        />
      ) : null}

      {reveal.kind === 'numeric' ? (
        <div className="gg-rung-reveal-pair">
          <div className="gg-rung-reveal-side" data-tone="correct">
            <span className="gg-rung-reveal-tag">{NUMERIC_SPECS[reveal.quantity].unit}</span>
            <span className="gg-rung-reveal-answer tabular-nums">
              {numericText(reveal.quantity, reveal.correctValue)}
            </span>
          </div>
          <div
            className="gg-rung-reveal-side"
            data-tone={reveal.band === 'miss' ? 'wrong' : 'near'}
          >
            <span className="gg-rung-reveal-tag">
              {reveal.band === 'exact'
                ? 'Exactly right'
                : reveal.band === 'near'
                  ? 'Close'
                  : 'You said'}
            </span>
            <span className="gg-rung-reveal-answer tabular-nums">
              {reveal.pickedValue === null
                ? 'nothing'
                : numericText(reveal.quantity, reveal.pickedValue)}
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}
